import React, { ReactNode, useEffect, useRef, useState } from 'react';
import {
	AccessibilityInfo,
	Animated,
	Easing,
	Platform,
	StyleProp,
	StyleSheet,
	ViewStyle,
} from 'react-native';

import { Pressable } from '../common/Touchable';
import CardPlate from './CardPlate';
import { FILL, PRESS_IN_MS, PRESS_OUT_MS, PRESS_SCALE } from './cardPlateTokens';

type Props = {
	onPress: () => void;
	/** Size and content layout of the card; it needs a numeric width and height. */
	style: StyleProp<ViewStyle>;
	radius: number;
	strokeWidth: number;
	/** Screen pixels per Figma unit of glow blur and spread. */
	glowUnit: number;
	/** The large cards draw their outer glow at 70%. */
	outerGlowOpacity?: number;
	fill?: readonly [string, string];
	children: ReactNode;
};

const AnimatedPressable = Animated.createAnimatedComponent(Pressable);

const useReduceMotion = () => {
	const [reduceMotion, setReduceMotion] = useState(false);
	useEffect(() => {
		AccessibilityInfo.isReduceMotionEnabled().then(setReduceMotion);
		const sub = AccessibilityInfo.addEventListener('reduceMotionChanged', setReduceMotion);
		return () => sub.remove();
	}, []);
	return reduceMotion;
};

/** A home category card: the glowing plate (see cardPlateTokens.ts) plus its press state. */
const CategoryCard = ({
	onPress,
	style,
	radius,
	strokeWidth,
	glowUnit,
	outerGlowOpacity = 1,
	fill = FILL,
	children,
}: Props) => {
	const [pressed, setPressed] = useState(false);
	const scale = useRef(new Animated.Value(1)).current;
	const reduceMotion = useReduceMotion();
	const { width, height } = StyleSheet.flatten(style);

	const setPressedState = (next: boolean) => {
		setPressed(next);
		Animated.timing(scale, {
			toValue: next ? PRESS_SCALE : 1,
			duration: reduceMotion ? 0 : next ? PRESS_IN_MS : PRESS_OUT_MS,
			easing: Easing.out(Easing.ease),
			useNativeDriver: Platform.OS !== 'web',
		}).start();
	};

	return (
		<AnimatedPressable
			onPress={onPress}
			onPressIn={() => setPressedState(true)}
			onPressOut={() => setPressedState(false)}
			style={[style, { transform: [{ scale }] }]}
		>
			<CardPlate
				pressed={pressed}
				width={width as number}
				height={height as number}
				radius={radius}
				strokeWidth={strokeWidth}
				glowUnit={glowUnit}
				outerGlowOpacity={outerGlowOpacity}
				fill={fill}
				animate={!reduceMotion}
			/>
			{children}
		</AnimatedPressable>
	);
};

export default CategoryCard;
